/**
 * Evaluate simple math expressions in input fields.
 * Supports: +, -, *, /, parentheses, and decimal numbers.
 * Returns the evaluated result, or the original number if not an expression.
 * Returns null if the input is invalid.
 */
export function evalMathInput(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // If it's just a plain number, return it directly
  const plain = Number(trimmed);
  if (!isNaN(plain)) return plain;

  // Only allow safe characters: digits, operators, parens, dots, spaces
  if (!/^[\d\s+\-*/().]+$/.test(trimmed)) return null;

  try {
    // Use Function constructor for safe math eval (no access to scope)
    const result = new Function(`"use strict"; return (${trimmed})`)();
    if (typeof result === 'number' && isFinite(result)) return result;
    return null;
  } catch {
    return null;
  }
}

/**
 * Convert inches to a human-readable feet + inches string.
 * e.g., 576 → "48'-0\"", 54 → "4'-6\""
 */
export function inchesToFeetDisplay(inches: number): string {
  if (inches <= 0) return '0\'';
  const ft = Math.floor(inches / 12);
  const rem = Math.round(inches % 12);
  if (rem === 0) return `${ft}'-0"`;
  return `${ft}'-${rem}"`;
}
